// WHAT: Daily Stats Card Component (Molecule)
// WHY:  Displays daily nutrition and activity overview with progress rings and stats
// DOMAIN: Health → Yazio
// DEPENDENCIES: ProgressRing atom, shared utils, DailyStatsCardProps type
// RISKS:
//   - NOTE: Date formatting uses German locale (de-DE) - hardcoded
//   - NOTE: Calories percentage is clamped to 100% max (over goal not shown in ring)
//   - NOTE: Water and steps display format is fixed (L for liters, locale string for steps)

import type { DailyStatsCardProperties } from "../../types";

import { cn } from "@/shared/utils";

import { ProgressRing } from "../atoms/ProgressRing";

/**
 * WHAT:
 *   Card component displaying daily nutrition and activity statistics.
 *
 * WHY:
 *   Provides comprehensive daily overview with visual progress indicators
 *   for calories, water intake, and steps.
 *
 * PROPS:
 *   - totalCalories: number — Total calories consumed today
 *   - caloriesGoal: number — Daily calorie goal
 *   - waterIntake: number — Water intake in liters
 *   - steps: number — Steps taken today
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Calculates calories percentage and remaining calories
 *   - Displays calories progress ring with color coding (red/yellow/green)
 *   - Shows current date in German locale format
 *   - Displays water intake with icon and liters unit
 *   - Shows steps count with locale string formatting
 *   - Responsive grid layout (1 col mobile, 2 col desktop)
 *
 * RISKS:
 *   - NOTE: Calories percentage clamped to 100% (over goal not visible in ring)
 *   - NOTE: Date formatting is hardcoded to German locale
 *   - NOTE: Water and steps formatting is fixed (no unit customization)
 *
 * @param root0
 *
 * @param root0.totalCalories
 *
 * @param root0.caloriesGoal
 *
 * @param root0.waterIntake
 *
 * @param root0.steps
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Daily stats card component
 *
 */
export function DailyStatsCard({
  caloriesGoal,
  className,
  steps,
  totalCalories,
  waterIntake,
}: Readonly<DailyStatsCardProperties>) {
  const caloriesPercentage = caloriesGoal > 0 ? (totalCalories / caloriesGoal) * 100 : 0;
  const caloriesRemaining = caloriesGoal - totalCalories;
  const isOverGoal = totalCalories > caloriesGoal;

  const getCaloriesColor = () => {
    if (isOverGoal) {
      return "red";
    }
    if (caloriesPercentage >= 90) {
      return "yellow";
    }
    return "green";
  };

  return (
    <div
      className={cn(
        "bg-slate-800/50 rounded-lg p-6 border border-slate-700",
        typeof className === "string" ? className : undefined,
      )}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Today&apos;s Overview</h2>
        <div className="text-sm text-slate-400">
          {new Date().toLocaleDateString("de-DE", {
            day: "numeric",
            month: "long",
            weekday: "long",
            year: "numeric",
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calories Progress Ring */}
        <div className="flex flex-col items-center space-y-4">
          <ProgressRing
            color={getCaloriesColor()}
            percentage={Math.min(caloriesPercentage, 100)}
            size={140}
          >
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {typeof totalCalories === "number" ? String(Math.round(totalCalories)) : "0"}
              </div>
              <div className="text-sm text-slate-400">kcal</div>
            </div>
          </ProgressRing>

          <div className="text-center">
            <div className="text-lg font-semibold text-white">
              {isOverGoal
                ? "Over Goal"
                : (() => {
                    if (typeof caloriesRemaining === "number") {
                      return `${String(Math.round(caloriesRemaining))} kcal left`;
                    }
                    return "0 kcal left";
                  })()}
            </div>
            <div className="text-sm text-slate-400">
              Goal: {typeof caloriesGoal === "number" ? String(Math.round(caloriesGoal)) : "0"} kcal
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
              <div>
                <div className="text-sm text-slate-400">Water Intake</div>
                <div className="text-lg font-semibold text-white">
                  {typeof waterIntake === "number" ? `${waterIntake.toFixed(1)} L` : "N/A"}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
              <div>
                <div className="text-sm text-slate-400">Steps</div>
                <div className="text-lg font-semibold text-white">
                  {typeof steps === "number" ? steps.toLocaleString() : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
